import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import IconButton from 'fe-design-base/molecules/IconButton';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';

import {
  getCurrentLocationName,
  getCurrentLocationUuid,
} from 'selectors/session';

import { getOpenShiftDetails } from 'features/notifications/api';
import { ClaimUserCard } from 'features/notifications/components/ClaimUserCard';
import { ShiftCard } from 'features/notifications/components/ShiftCard';
import { calculateScrollHeightWithRefs } from 'features/notifications/utils';

import { dateStringToSeconds } from 'util/dateTime';
import { toI18n } from 'util/i18n';

type OpenShiftDetailsProps = {
  requestId: string;
  onClose: () => void;
};

const OpenShiftDetails = ({ requestId, onClose }: OpenShiftDetailsProps) => {
  const shiftId = requestId.replace('claim-', '');
  const [openShiftDetails, setOpenShiftDetails] = useState<any>(null);
  const locationName = useSelector(getCurrentLocationName);
  const locationId = useSelector(getCurrentLocationUuid);

  const headerRef = useRef<HTMLElement>(null);
  const shiftCardRef = useRef<HTMLElement>(null);
  const [scrollHeight, setScrollHeight] = useState('100vh');

  useEffect(() => {
    getOpenShiftDetails(shiftId).then(setOpenShiftDetails);
  }, [shiftId]);

  useEffect(() => {
    const handleCalculateScrollHeight = () => {
      calculateScrollHeightWithRefs(headerRef, shiftCardRef, setScrollHeight);
    };

    handleCalculateScrollHeight();

    window.addEventListener('resize', handleCalculateScrollHeight);

    return () => {
      window.removeEventListener('resize', handleCalculateScrollHeight);
    };
  }, []);

  const location = useMemo(
    () => ({
      id: locationId,
      name: locationName,
    }),
    [locationId, locationName]
  );

  const shift = useMemo(() => {
    if (!openShiftDetails) return null;

    const { end_at, start_at, role } = openShiftDetails;
    return {
      start_at_secs: dateStringToSeconds(start_at),
      end_at_secs: dateStringToSeconds(end_at),
      role_name: role?.name,
    };
  }, [openShiftDetails]);

  if (!openShiftDetails) {
    return (
      <Box column h="100vh" vcenter hcenter>
        <CircularInfinite size="large" />
      </Box>
    );
  }

  const { claims } = openShiftDetails;

  return (
    <Box relative column h="100%">
      <Box
        ref={headerRef}
        flex
        row
        alignItemsCenter
        justifyContentSpaceBetween
        bbw={2}
        bcolor="mono300"
        p={16}
        ph={32}
      >
        <IconButton
          icon="ArrowLeft"
          label={toI18n('actions.go_back')}
          onClick={onClose}
        />
        <Text
          variant="bodyBold"
          i18n="requests_feed.open_shift.details.title"
        />
        <div style={{ width: '40px' }} />
      </Box>

      <Box ref={shiftCardRef}>
        <ShiftCard shift={shift!} location={location} />
      </Box>

      <Box scrollY h={scrollHeight} grow>
        <Box flex column gap={8} p={16} bgcolor="mono100">
          {claims.map((claim: any) => (
            <ClaimUserCard key={claim.id} claim={claim} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default OpenShiftDetails;
